import * as React from 'react';

import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';

import { SpinnerIcon } from './spinner';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-0 rounded-md text-sm font-medium whitespace-nowrap ring-offset-background transition select-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        primary:
          'button-primary-surface -m-px text-primary-foreground shadow-[0_2px_3px_0_var(--primary-shadow)] ring-1 ring-primary active:scale-[0.97] [&_svg]:text-primary-foreground',
        secondary: 'bg-secondary text-primary hover:bg-accent [&_svg]:text-primary',
        'secondary-neutral':
          'bg-secondary text-foreground hover:bg-accent [&_svg]:text-muted-foreground',
        'outline-neutral':
          'border border-border bg-background text-foreground hover:bg-selected-soft [&_svg]:text-muted-foreground',
        'outline-black':
          'border border-border bg-background text-foreground hover:bg-selected-soft',
        info: 'border border-border bg-background text-muted-foreground hover:bg-selected-soft',
        ghost: 'text-primary hover:bg-selected-soft [&_svg]:text-primary',
        'ghost-neutral': 'text-foreground hover:bg-selected-soft [&_svg]:text-muted-foreground',
        link: 'text-foreground hover:text-primary hover:underline [&_svg]:text-foreground',
        'link-secondary': 'text-muted-foreground hover:underline [&_svg]:text-muted-foreground',
        destructive:
          'bg-destructive-soft text-destructive hover:bg-destructive-soft focus-visible:ring-destructive-soft [&_svg]:text-destructive',
        'destructive-ghost':
          'text-destructive hover:bg-destructive-soft hover:text-destructive focus-visible:ring-destructive-soft [&_svg]:text-destructive',
      },
      size: {
        sm: 'h-9 min-w-16 px-2 py-1.5 [&_svg]:size-4',
        md: 'h-10 min-w-24 px-3 py-2 [&_svg]:size-4',
        // Inline text action, flush with its container: no padding (including the
        // label span's px-1 from withPaddedLabel below), no hover box.
        flush:
          'h-auto min-w-0 cursor-pointer gap-1 rounded-none p-0 leading-none hover:bg-transparent [&_svg]:size-4 [&>span]:px-0',
        'icon-xss': 'size-5 min-w-auto [&_svg]:size-3',
        // Same 20px box as icon-xss but a full-size glyph, for icons sitting inline with a
        // 20px line of text: at icon-sm the row grows past the line height and pushes the
        // content below it down.
        'icon-xs': 'size-5 min-w-auto rounded-sm [&_svg]:size-4',
        'icon-sm': 'size-8 min-w-auto [&_svg]:size-4',
        'icon-md': 'size-9 min-w-auto [&_svg]:size-4',
        'icon-lg': 'size-10 min-w-auto [&_svg]:size-4',
      },
    },
    compoundVariants: [
      { variant: 'primary', size: 'md', className: 'px-2' },
      { variant: ['link', 'link-secondary'], size: ['sm', 'md'], className: 'min-w-0' },
    ],
    defaultVariants: { variant: 'primary', size: 'md' },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  isLoading?: boolean;
}

// The label carries the px-1 padding (the flex container is gap-0). Wrap the contiguous
// text run in a single span so a multi-part label like "Filters (1)" gets one padding, not
// one per word. Icons stay as bare flex siblings.
const withPaddedLabel = (children: React.ReactNode) => {
  const result: React.ReactNode[] = [];
  let label: React.ReactNode[] = [];

  const flushLabel = (key: React.Key) => {
    if (label.length > 0) {
      result.push(
        <span className="px-1" key={key}>
          {label}
        </span>
      );
      label = [];
    }
  };

  // toArray assigns stable keys to element children and drops null/boolean/undefined.
  React.Children.toArray(children).forEach((child, index) => {
    if (typeof child === 'string' || typeof child === 'number') {
      label.push(child);
    } else {
      flushLabel(`label-${index}`);
      result.push(child);
    }
  });
  flushLabel('label-end');

  return result;
};

const Button = ({
  className,
  variant,
  size,
  asChild = false,
  isLoading = false,
  disabled,
  ref,
  ...props
}: ButtonProps & { ref?: React.Ref<HTMLButtonElement> }) => {
  const Comp = asChild ? Slot : 'button';
  // asChild hands rendering to the child, so it has no loading state.
  const loading = isLoading && !asChild;
  const children = asChild
    ? props.children
    : [loading && <SpinnerIcon key="spinner" />, ...withPaddedLabel(props.children)];
  return (
    <Comp
      className={cn(
        buttonVariants({ variant, size, className }),
        // The spinner takes the place of the icons, so the button never shows two.
        loading && '[&>svg]:hidden'
      )}
      ref={ref}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...props}
    >
      {children}
    </Comp>
  );
};

export { Button, buttonVariants };
